import { useSticky } from "@/hooks/useSticky";
import { MetaRatingIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { AlternativeVersion } from "@/pages/DetailedPlayer/sections/AlternativeVersionsSection";
import { tierSelector } from "@/redux/players/playersSlice";
import { zeroChemMetaRating } from "@repo/shared/hooks";
import { comparePlayerKey } from "@repo/shared/redux";
import { DetailedPlayer } from "@repo/shared/types";
import { useSelector } from "react-redux";

/**
 * The sticky-header trigger both hosts share: sentinel-after-cards flips
 * `scrolledPast`, and `stickyClassName` carries the pinned styling (set only
 * while stuck). Hosts differ just in where the bar pins — the navbar offset
 * on the routed desktop/mobile page, the scroll-container top in the sheet /
 * phone shell.
 */
export const useCompareStickyHeader = ({
  offset,
  withinScrollContainer,
  topClassName,
}: {
  offset: number;
  withinScrollContainer: boolean;
  topClassName: string;
}) => {
  const [scrolledPast, sentinelRef] = useSticky({
    triggerOn: "bottom",
    offset,
    withinScrollContainer,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];
  return {
    scrolledPast,
    sentinelRef,
    stickyClassName: scrolledPast
      ? cn("sticky z-20 shadow-soft-dark-bottom", topClassName)
      : undefined,
  };
};

/**
 * The compressed players on the column axis — the sticky bar's content on
 * both hosts. Desktop renders it bare on the 280px grid; mobile hides the
 * ratings, clips each cell and threads `rowRef`/`style` to mirror the
 * horizontal scroller with a transform.
 */
export const CompareStickyPlayersRow = ({
  players,
  gridStyle,
  gradedRoleId,
  hideRatings,
  rowRef,
  className,
  style,
}: {
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
  /** The selected graded role (tier basis); each player's own when absent. */
  gradedRoleId?: number;
  hideRatings?: boolean;
  rowRef?: React.Ref<HTMLDivElement>;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <div
    ref={rowRef}
    className={cn("grid", className)}
    style={{ ...gridStyle, ...style }}
  >
    {players.map((player) => (
      <div
        key={comparePlayerKey(player)}
        className="flex items-center justify-center overflow-hidden"
      >
        <CompareStickyPlayer
          player={player}
          tierMetaRating={zeroChemMetaRating(player, gradedRoleId)}
          hideRatings={hideRatings}
        />
      </div>
    ))}
  </div>
);

/**
 * Compressed player shown in the sticky header once the cards scroll behind the
 * navbar: mini rating badge + name + meta rating + tier. Mirrors
 * `TopSectionCompressed` from the DetailedPlayer page.
 */
export const CompareStickyPlayer = ({
  player,
  tierMetaRating,
  hideRatings = false,
}: {
  player: DetailedPlayer;
  /** Rating the tier is graded from (tiers are always 0-chem). */
  tierMetaRating?: number;
  /** Mobile columns are too narrow for everything — badge + name only. */
  hideRatings?: boolean;
}) => {
  const tier = useSelector(
    tierSelector(
      tierMetaRating ?? player.metaRating,
      player.playerRoleId,
      true,
    ),
  );
  return (
    // min-w-0 + truncate: on mobile's narrow columns the name gives way
    // instead of pushing the rating/tier out of the clipped cell.
    <div className="flex min-w-0 max-w-full flex-row items-center gap-x-2">
      <AlternativeVersion
        version={{
          versionId: player.versionId,
          metalId: player.metalId,
          rating: player.rating,
          resourceId: player.resourceId,
        }}
        playerName={player.cardName}
        isActive
      />
      <p className="text-dark-primary truncate text-sm font-semibold">
        {player.cardName}
      </p>
      {!hideRatings && (
        <div className="text-dark-tertiary flex shrink-0 flex-row items-center gap-x-2">
          <p className="text-dark-primary text-sm font-semibold">
            {player.metaRating?.toFixed(1)}
          </p>
          <MetaRatingIcon width="16" height="16" />
          <div className="bg-dark-disabled h-5 w-0.5 rounded-md" />
          <p className={cn("text-base font-bold", tier?.tierTextColor)}>
            {tier?.tierName}
          </p>
        </div>
      )}
    </div>
  );
};
